import {useDeck} from "../../deck-management/context/DeckContext";
import {useEffect, useState} from "react";
import {Session, SessionItem} from "../types/sessionTypes";
import {useApiRequest} from "../../../common/hooks/useApiRequest";
import {useNavigate} from "react-router-dom";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {ApiError} from "../../../common/types/requests";

export interface UseReviewReturn {
    session: Session | null
    displaySessionSelectionDialog: boolean
    startNewSession: () => void
    continueExistingSession: () => void
    loadingMessage: string | null
    isLoadError: boolean
    submitReviewItem: (item: SessionItem, fails: number) => void
    isSubmitReviewItemError: boolean
    remainingItemCount: number
    finishedItemCount: number
    isResyncing: boolean
    finishReviewSession: () => void
}

function UseReview() : UseReviewReturn {

    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { currentDeck, ready : deckDataReady, isError: isDeckLoadError } = useDeck();

    const [session, setSession] = useState<Session | null>(null);
    const [sessionStarted, setSessionStarted] = useState<boolean>(false);
    const [displaySessionSelectionDialog, setDisplaySessionSelectionDialog] = useState<boolean>(false);
    const [remainingItemCount, setRemainingItemCount] = useState<number>(0);
    const [finishedItemCount, setFinishedItemCount] = useState<number>(0);
    const [isSubmitReviewItemError, setIsSubmitReviewItemError] = useState<boolean>(false);

    const {getRequest, postRequest} = useApiRequest();

    const { data: currentSession, isFetching: isFetchingSession, isPending: isSessionDataPending, isSuccess: isLoadSessionSuccess, isError: isLoadSessionError } = useQuery({
        queryKey: ["review", currentDeck?.deckId],
        queryFn: () => getRequest<Session | {}>(`decks/${currentDeck?.deckId}/review/session/current`),
        enabled: deckDataReady,
    });

    const { mutate: startNewSession, isPending: isSessionStartPending, isError: isStartSessionError } = useMutation({
        mutationFn: () => postRequest<Session>(`decks/${currentDeck?.deckId}/review/session/start`),
        onMutate: () => setDisplaySessionSelectionDialog(false),
        onSuccess: (sessionData: Session) => {
            setSession(sessionData);
            setSessionStarted(true);
            setRemainingItemCount(sessionData.items.length);
            setFinishedItemCount(0)
        }
    })

    const { mutate: postCompletedReview } = useMutation({
        mutationFn: ({item, fails}: {item: SessionItem, fails: number}) => {
            return postRequest(`decks/${currentDeck?.deckId}/review/session/${session?.sessionId}/items/${item.seq}/submit`, {fails});
        },
        onSuccess: () => {
            setRemainingItemCount(prev => prev - 1);
            setFinishedItemCount(prev => prev + 1);
        },
        onError: (e: ApiError) => {
            if ((e)?.code === "ITEM_ALREADY_COMPLETED" || e?.code === "SESSION_NOT_FOUND") {
                queryClient.invalidateQueries({ queryKey: ["review", currentDeck?.deckId],});
            } else {
                setIsSubmitReviewItemError(true);
            }
        },
    });


    useEffect(() => {
        if (!isLoadSessionSuccess || isFetchingSession)
            return;

        if (sessionStarted) {
            if (Object.keys(currentSession).length === 0) {
                finishReviewSession();
            } else {
                setStateDataFromSession(currentSession as Session);
            }
        }
        else if (Object.keys(currentSession).length === 0) {
            startNewSession();
        } else {
            setDisplaySessionSelectionDialog(true);
        }
    }, [currentSession, isLoadSessionSuccess, isFetchingSession])


    const continueExistingSession = () => {
        if (session)
            throw new Error("Session already exists when trying to use an existing one.");
        if (!currentSession || Object.keys(currentSession).length === 0)
            throw new Error("No existing session to use.");

        setDisplaySessionSelectionDialog(false);
        setSessionStarted(true);
        setStateDataFromSession(currentSession as Session);
    }

    const setStateDataFromSession = (session: Session) => {
        setSession(session);
        setRemainingItemCount(session.items.filter(item => item.status === 'ready' || item.status === 'pending').length);
        setFinishedItemCount(session.items.filter(item => item.status === 'done').length);
    };

    useEffect(() => {
        if (isLoadSessionSuccess && session && remainingItemCount === 0) {
            finishReviewSession();
        }
    }, [remainingItemCount, session, isLoadSessionSuccess])

    const finishReviewSession = () => {
        if (!session || session.words.length === 0 || finishedItemCount === 0){
            navigate('/');
            return;
        }
        navigate('/summary', {
            state: {
                sessionId: session.sessionId,
            },
            replace: true
        });
    }

    const loadingMessage =
        !deckDataReady ? "Loading deck information" :
        isSessionDataPending ? "Loading session" :
        isSessionStartPending ? "Starting session" :
        session && remainingItemCount === 0 ? "Wrapping up" : null;

    return {
        session,
        startNewSession,
        continueExistingSession,
        displaySessionSelectionDialog,
        submitReviewItem: (item, fails) => postCompletedReview({item, fails}),
        loadingMessage,
        isLoadError: isDeckLoadError || isLoadSessionError || isStartSessionError,
        isSubmitReviewItemError,
        remainingItemCount,
        finishedItemCount,
        isResyncing: sessionStarted && isFetchingSession,
        finishReviewSession,
    }

}

export default UseReview;